import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Tag, MapPin, Navigation, Send, Upload, X, ShieldCheck, Car, Wrench, Cog, Paintbrush, FileSearch, Truck, CircleDot, HelpCircle, Zap, Snowflake, Droplets, Umbrella } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";
import { trUpper } from "../lib/labels";
import { useAuth } from "../context/AuthContext";
import { QuoteLifecycleNote } from "../components/QuoteLifecycle";

const inputCls = "w-full px-4 py-2.5 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";
const CAT_ICON = { arac_satis: Car, ekspertiz: FileSearch, servis_tamir: Wrench, cam_filmi: ShieldCheck, yedek_parca: Cog, cekici: Truck, lastik: CircleDot, kaporta_boya: Paintbrush, elektrik: Zap, klima: Snowflake, yikama: Droplets, sigorta: Umbrella, diger: HelpCircle };

const EMPTY = { vehicleId: "", vin: "", brand: "", model: "", year: "", km: "", plate: "", description: "", photos: [], expected_price: "", part_name: "", city: "", district: "", share_contact: false, from_city: "", from_district: "", to_city: "", to_district: "" };

export default function TeklifAl() {
  const [cats, setCats] = useState([]);
  const [catKey, setCatKey] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [form, setForm] = useState({ ...EMPTY });
  const [loc, setLoc] = useState(null);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [locMode, setLocMode] = useState("farkli");
  const [towScope, setTowScope] = useState("");
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasRegLoc = !!(user && user.city);

  useEffect(() => {
    api.get("/teklif-kategorileri").then((r) => setCats(r.data)).catch(() => {});
    api.get("/my-vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (hasRegLoc) {
      setLocMode("kayitli");
      setForm((f) => ({ ...f, city: user.city, district: user.district || "" }));
    }
  }, [hasRegLoc]);

  const sel = cats.find((c) => c.key === catKey);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pickVehicle = (vid) => {
    const v = vehicles.find((x) => x.id === vid);
    setForm((f) => ({ ...f, vehicleId: vid, vin: v ? v.vin : "", brand: v ? v.brand : "", model: v ? v.model : "", year: v ? v.year : "", km: v ? v.km : "", plate: v ? v.plate || "" : "" }));
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error("Konum bilgisi alınamadı. Lütfen cihazınızın konum iznini açın."); return; }
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        setLoc({ lat: p.coords.latitude, lng: p.coords.longitude });
        try {
          const { data } = await api.post("/geo/reverse", { lat: p.coords.latitude, lng: p.coords.longitude });
          if (data && data.province) {
            if (locMode === "farkli") setForm((f) => ({ ...f, city: trUpper(data.province), district: trUpper(data.district || "") }));
            toast.success(`Konum algılandı: ${data.province}${data.district ? ` / ${data.district}` : ""}`);
          } else {
            toast.success("Konumunuz alındı — mesafe bilgisi işletmelere gösterilir");
          }
        } catch {
          toast.success("Konumunuz alındı — mesafe bilgisi işletmelere gösterilir");
        }
      },
      () => toast.error("Konum bilgisi alınamadı. Lütfen cihazınızın konum iznini açın."), { timeout: 10000 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!catKey) { toast.error("Önce hizmet kategorisi seçin"); return; }
    if (catKey === "cekici" && !towScope) { toast.error("Lütfen şehir içi veya şehirler arası çekici seçin"); return; }
    const isTowInter = catKey === "cekici" && towScope === "sehirler_arasi";
    setSending(true);
    try {
      const { data } = await api.post("/teklif/talep", {
        category: catKey, vin: form.vin, brand: form.brand, model: form.model,
        year: form.year === "" ? null : Number(form.year), km: form.km === "" ? null : Number(form.km),
        plate: form.plate, description: form.description, photos: form.photos,
        expected_price: form.expected_price === "" ? null : Number(form.expected_price),
        part_name: form.part_name,
        city: isTowInter ? form.from_city : form.city,
        district: isTowInter ? form.from_district : form.district,
        lat: loc?.lat || 0, lng: loc?.lng || 0, share_contact: form.share_contact,
        location_mode: locMode,
        tow_scope: catKey === "cekici" ? towScope : "",
        from_city: isTowInter ? form.from_city : "",
        from_district: isTowInter ? form.from_district : "",
        to_city: isTowInter ? form.to_city : "",
        to_district: isTowInter ? form.to_district : "",
      });
      toast.success(data.matched_count > 0
        ? `Talebiniz ${data.matched_count} işletmeye iletildi`
        : "Talebiniz kaydedildi; kriterlere uyan işletme bulunamadı (bölge/konum genişletebilirsiniz)");
      navigate("/tekliflerim");
    } catch (err) { toast.error(formatApiError(err)); }
    setSending(false);
  };

  return (
    <div data-testid="teklif-al-page" className="min-h-screen pt-20 sm:pt-24 pb-16">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-6">
        <div className="flex items-center gap-3">
          <Tag className="w-6 h-6 text-emerald-300" />
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Teklif Talebi Oluştur</h1>
        </div>
        <p className="text-sm text-slate-400">Hizmeti seçin; talebiniz yalnızca ilçenizdeki ilgili onaylı işletmelere iletilir, teklifleri karşılaştırırsınız.</p>
        <QuoteLifecycleNote testid="teklif-al-lifecycle-note" />

        <div className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7">
          <label className={labelCls}>Hizmet Seçin *</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
            {cats.map((c) => {
              const Icon = CAT_ICON[c.key] || HelpCircle;
              return (
                <button key={c.key} type="button" data-testid={`quote-cat-${c.key}`}
                  onClick={() => { setCatKey(catKey === c.key ? "" : c.key); setTowScope(""); }}
                  className={`flex items-center gap-2.5 p-3.5 rounded-xl border text-left transition-colors ${
                    catKey === c.key ? "border-emerald-500 bg-emerald-500/10 shadow-[0_0_14px_rgba(16,185,129,0.25)]" : "border-slate-300 shadow-sm hover:border-emerald-500 hover:bg-cyan-50/70 hover:shadow-md"}`}>
                  <Icon className={`w-5 h-5 shrink-0 ${catKey === c.key ? "text-emerald-300" : "text-slate-400"}`} />
                  <span className={`text-xs font-semibold ${catKey === c.key ? "text-emerald-200" : "text-slate-300"}`}>{c.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {catKey && (
          <form onSubmit={submit} data-testid="quote-request-form"
            className="rounded-2xl border border-emerald-500/20 bg-slate-900/60 backdrop-blur-xl p-5 sm:p-7 space-y-4 animate-fade-up">
            <h2 className="text-lg font-semibold text-slate-100">{sel?.label}</h2>

            {catKey === "cekici" && (
              <div data-testid="tow-scope-question" className="rounded-xl border border-cyan-500/25 bg-slate-950/50 p-4 space-y-3">
                <p className="text-sm font-semibold text-slate-200">Çekici hizmetini şehir içinde mi, şehirler arasında mı almak istiyorsunuz?</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <button type="button" data-testid="tow-scope-sehir-ici" onClick={() => setTowScope("sehir_ici")}
                    className={`p-4 rounded-xl border text-left transition-colors ${towScope === "sehir_ici" ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 hover:border-cyan-400/30"}`}>
                    <span className={`text-sm font-bold ${towScope === "sehir_ici" ? "text-cyan-200" : "text-slate-200"}`}>ŞEHİR İÇİ ÇEKİCİ</span>
                    <span className="block text-[11px] text-slate-500 mt-1">Yalnızca seçilen ilçedeki uygun çekicilere iletilir</span>
                  </button>
                  <button type="button" data-testid="tow-scope-sehirler-arasi"
                    onClick={() => { setTowScope("sehirler_arasi"); setForm((f) => ({ ...f, from_city: f.from_city || f.city, from_district: f.from_district || f.district })); }}
                    className={`p-4 rounded-xl border text-left transition-colors ${towScope === "sehirler_arasi" ? "border-cyan-400/60 bg-cyan-400/10" : "border-white/10 hover:border-cyan-400/30"}`}>
                    <span className={`text-sm font-bold ${towScope === "sehirler_arasi" ? "text-cyan-200" : "text-slate-200"}`}>ŞEHİRLER ARASI ÇEKİCİ</span>
                    <span className="block text-[11px] text-slate-500 mt-1">Türkiye genelindeki uygun çekicilere bildirim gider</span>
                  </button>
                </div>
              </div>
            )}

            <div><label className={labelCls}>Aracım</label>
              <select data-testid="quote-vehicle" value={form.vehicleId} onChange={(e) => pickVehicle(e.target.value)} className={inputCls}>
                <option value="">Manuel giriş / kayıtlı olmayan araç</option>
                {vehicles.map((v) => (
                  <option key={v.id} value={v.id}>{`${v.brand} ${v.model} — ${v.plate || v.vin}`}</option>
                ))}
              </select></div>

            <div className="grid grid-cols-2 gap-4">
              <div><label className={labelCls}>Marka *</label>
                <input data-testid="quote-brand" required value={form.brand} onChange={(e) => set("brand", trUpper(e.target.value))} className={inputCls} placeholder="Ford" /></div>
              <div><label className={labelCls}>Model *</label>
                <input data-testid="quote-model" required value={form.model} onChange={(e) => set("model", trUpper(e.target.value))} className={inputCls} placeholder="Courier" /></div>
              <div><label className={labelCls}>Model Yılı</label>
                <input data-testid="quote-year" type="number" min="1950" max="2030" value={form.year} onChange={(e) => set("year", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Kilometre</label>
                <input data-testid="quote-km" type="number" min="0" value={form.km} onChange={(e) => set("km", e.target.value)} className={inputCls} /></div>
              <div><label className={labelCls}>Plaka</label>
                <input data-testid="quote-plate" value={form.plate} onChange={(e) => set("plate", trUpper(e.target.value))} className={inputCls} /></div>
              <div><label className={labelCls}>Şasi No / VIN</label>
                <input data-testid="quote-vin" value={form.vin} onChange={(e) => set("vin", e.target.value.toUpperCase())} className={`${inputCls} font-mono-vin tracking-widest`} /></div>
            </div>

            {sel?.extras.includes("expected_price") && (
              <div><label className={labelCls}>Satış Fiyatı Beklentiniz (₺, opsiyonel)</label>
                <input data-testid="quote-expected-price" type="number" min="0" value={form.expected_price} onChange={(e) => set("expected_price", e.target.value)} className={inputCls} placeholder="örn. 650000" /></div>
            )}
            {sel?.extras.includes("part_name") && (
              <div><label className={labelCls}>İhtiyaç Duyulan Parça *</label>
                <input data-testid="quote-part-name" required value={form.part_name} onChange={(e) => set("part_name", trUpper(e.target.value))} className={inputCls} placeholder="örn. Ön fren balatası, triger seti..." /></div>
            )}

            <div><label className={labelCls}>Açıklama / Arıza / Hasar-Tramer Bilgisi</label>
              <textarea data-testid="quote-description" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)}
                className={inputCls} placeholder="Durumu detaylı anlatın; varsa ekspertiz/OBD raporu özetini ekleyin..." /></div>

            {catKey === "cekici" && towScope === "sehirler_arasi" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className={labelCls}>Aracın Alınacağı İl *</label>
                  <input data-testid="tow-from-city" required value={form.from_city} onChange={(e) => set("from_city", trUpper(e.target.value))} className={inputCls} placeholder="Kocaeli" /></div>
                <div><label className={labelCls}>Alınacağı İlçe *</label>
                  <input data-testid="tow-from-district" required value={form.from_district} onChange={(e) => set("from_district", trUpper(e.target.value))} className={inputCls} placeholder="Çayırova" /></div>
                <div><label className={labelCls}>Götürüleceği İl *</label>
                  <input data-testid="tow-to-city" required value={form.to_city} onChange={(e) => set("to_city", trUpper(e.target.value))} className={inputCls} placeholder="Ankara" /></div>
                <div><label className={labelCls}>Götürüleceği İlçe *</label>
                  <input data-testid="tow-to-district" required value={form.to_district} onChange={(e) => set("to_district", trUpper(e.target.value))} className={inputCls} placeholder="Çankaya" /></div>
              </div>
            ) : (
              <>
                <div>
                  <label className={labelCls}>Hizmet Konumu *</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <button type="button" data-testid="loc-mode-kayitli" disabled={!hasRegLoc}
                      onClick={() => { setLocMode("kayitli"); setForm((f) => ({ ...f, city: user.city, district: user.district || "" })); }}
                      className={`p-3.5 rounded-xl border text-left transition-colors disabled:opacity-40 ${locMode === "kayitli" && hasRegLoc ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 hover:border-emerald-400/30"}`}>
                      <span className={`text-xs font-bold ${locMode === "kayitli" && hasRegLoc ? "text-emerald-200" : "text-slate-200"}`}>Kayıtlı Konumum</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">{hasRegLoc ? `${user.city}${user.district ? ` / ${user.district}` : ""}` : "Üyeliğinizde kayıtlı konum yok"}</span>
                    </button>
                    <button type="button" data-testid="loc-mode-farkli" onClick={() => setLocMode("farkli")}
                      className={`p-3.5 rounded-xl border text-left transition-colors ${locMode === "farkli" || !hasRegLoc ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 hover:border-emerald-400/30"}`}>
                      <span className={`text-xs font-bold ${locMode === "farkli" || !hasRegLoc ? "text-emerald-200" : "text-slate-200"}`}>Farklı Konum Seç</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">Hizmet almak istediğiniz il / ilçe</span>
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1.5">Seçilen konum yalnızca bu talep için kullanılır; üyelik bilgileriniz değişmez.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>İl *</label>
                    <input data-testid="quote-city" required disabled={locMode === "kayitli" && hasRegLoc} value={form.city} onChange={(e) => set("city", trUpper(e.target.value))} className={`${inputCls} disabled:opacity-60`} placeholder="Kocaeli" /></div>
                  <div><label className={labelCls}>İlçe *</label>
                    <input data-testid="quote-district" required disabled={locMode === "kayitli" && hasRegLoc} value={form.district} onChange={(e) => set("district", trUpper(e.target.value))} className={`${inputCls} disabled:opacity-60`} placeholder="Çayırova" /></div>
                </div>
              </>
            )}
            <button type="button" data-testid="quote-use-location" onClick={useMyLocation}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors">
              <Navigation className="w-4 h-4" /> {loc ? "Konum Alındı ✓" : "Konumumu Kullan (mesafe gösterimi)"}
            </button>

            <div>
              <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
                onChange={async (e) => {
                  setUploading(true);
                  try { set("photos", [...form.photos, ...(await uploadFiles([...e.target.files]))].slice(0, 6)); }
                  catch (err) { toast.error(formatApiError(err)); }
                  setUploading(false);
                  e.target.value = "";
                }} />
              <button type="button" data-testid="quote-photo-upload" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-dashed border-white/20 text-slate-300 text-sm hover:border-emerald-400/50 hover:text-emerald-300 transition-colors disabled:opacity-50">
                <Upload className="w-4 h-4" /> {uploading ? "Yükleniyor..." : "Fotoğraf Ekle (araç / parça / rapor)"}
              </button>
              {form.photos.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {form.photos.map((p) => (
                    <div key={p} className="relative">
                      <img src={photoUrl(p)} alt="" className="w-16 h-12 object-cover rounded-lg border border-white/10" />
                      <button type="button" onClick={() => set("photos", form.photos.filter((x) => x !== p))}
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <label data-testid="quote-share-contact" className="flex items-start gap-2.5 cursor-pointer rounded-xl border border-white/10 bg-slate-950/50 p-3.5">
              <input type="checkbox" checked={form.share_contact} onChange={(e) => set("share_contact", e.target.checked)} className="accent-emerald-400 w-4 h-4 mt-0.5" />
              <span className="text-xs text-slate-400 leading-relaxed">
                <span className="text-slate-200 font-semibold">İletişim bilgilerim (e-posta) talep oluşur oluşmaz işletmelerle paylaşılsın.</span><br />
                İşaretlenmezse iletişim bilgileriniz gizli kalır; yalnızca bir teklifle iletişime geçmeyi seçtiğinizde veya teklifi kabul ettiğinizde o işletmeyle paylaşılır.
              </span>
            </label>

            <button data-testid="quote-submit" type="submit" disabled={sending || uploading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-emerald-500 text-slate-950 font-bold hover:bg-emerald-400 transition-all disabled:opacity-50">
              <Send className="w-4 h-4" /> {sending ? "Gönderiliyor..." : "Teklif Talebini Gönder"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
